import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField,
  Typography, InputAdornment, Stack, Chip, FormControlLabel, Switch, CircularProgress, ToggleButton, ToggleButtonGroup, IconButton, Tooltip,
} from '@mui/material';
import { collection, getDocs, getFirestore, query, where, doc, getDoc } from 'firebase/firestore';
import firebaseApp from '../../../utils/firebaseConfig';
import { pickBestMember } from '../../../utils/members';
import { phoneDigits } from '../../../utils/phone';
import Iconify from '../../../components/iconify';
import {
  DEFAULT_LAYAWAY_SETTINGS, PAYMENT_METHOD, calcTaxCents, centsToDollars, dollarsToCents, formatPrice,
  layawayApi, toInputDate, printLayawayContract, printCustodyTransfer, CUSTODY_REASON,
} from '../../../utils/layaways';

const db = getFirestore(firebaseApp);

const FIREARM_TYPES = ['Pistola', 'Revólver', 'Rifle', 'Escopeta', 'Carabina', 'Otro'];

const todayInput = () => toInputDate(new Date());
const randomPin = () => String(Math.floor(1000 + Math.random() * 9000));
const emptyFirearm = () => ({ firearmType: 'Pistola', brand: '', model: '', caliber: '', serialNumber: '' });
const addDaysInput = (isoDate, days) => {
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + Number(days || 0));
  return toInputDate(d);
};

// Diálogo "Nuevo layaway": el staff lo llena cuando el cliente firma el
// contrato físico en tienda. Busca al cliente por TELÉFONO (regla del
// proyecto) y sugiere el depósito según el contrato (50% nueva / 75% usada),
// pero se puede escribir otro monto.
export default function LayawayCreateDialog({ open, onClose, onCreated, member }) {
  const [settings, setSettings] = useState(DEFAULT_LAYAWAY_SETTINGS);
  const [kind, setKind] = useState('layaway'); // layaway | custody (pedido Paola 2026-10-06)
  const [firearms, setFirearms] = useState([emptyFirearm()]); // custodia: varias armas
  const [initialMonths, setInitialMonths] = useState(1); // custodia: meses pagados hoy (se cobra desde el primero)
  const [custodyType, setCustodyType] = useState('standard'); // standard | deceased ($125/arma, incluye 180 días)
  // Documento "Traspaso a Armería" (Afía 2026-10-19): motivo (casilla del
  // documento), licencia de quien entrega y, si el propietario / fallecido es
  // otra persona, sus datos para la sección "Información del Fallecido /
  // Propietario del arma".
  const [custodyReason, setCustodyReason] = useState('temporary'); // temporary | deceased | expired_license
  const [licenseNumber, setLicenseNumber] = useState('');
  const [ownerDifferent, setOwnerDifferent] = useState(false);
  const [owner, setOwner] = useState({ name: '', address: '', phone: '', licenseNumber: '', shootingLicense: '', huntingLicense: '' });
  const [phone, setPhone] = useState('');
  const [lookupState, setLookupState] = useState('idle'); // idle | searching | found | notfound
  const [foundMember, setFoundMember] = useState(null);
  const [memberName, setMemberName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberAddress, setMemberAddress] = useState('');
  const [pin, setPin] = useState(randomPin()); // PIN de la cuenta nueva (si el cliente no existe)
  const [created, setCreated] = useState(null); // { id, layaway, accountCreated, pin } → paso final
  const [firearmType, setFirearmType] = useState('Pistola');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [caliber, setCaliber] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [condition, setCondition] = useState('new');
  const [contractNumber, setContractNumber] = useState('');
  const [price, setPrice] = useState('');
  const [deposit, setDeposit] = useState('');
  const [depositTouched, setDepositTouched] = useState(false);
  const [depositMethod, setDepositMethod] = useState('cash');
  const [startDate, setStartDate] = useState(todayInput());
  const [dueDate, setDueDate] = useState('');
  const [dueTouched, setDueTouched] = useState(false);
  const [notes, setNotes] = useState('');
  const [sendReceipt, setSendReceipt] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const s = await getDoc(doc(db, 'settings', 'layaways'));
        setSettings({ ...DEFAULT_LAYAWAY_SETTINGS, ...(s.exists() ? s.data() : {}) });
      } catch (e) { /* defaults */ }
    })();
    // reset
    setError(''); setSaving(false); setCreated(null); setMemberAddress(''); setPin(randomPin());
    setKind('layaway'); setFirearms([emptyFirearm()]); setInitialMonths(0);
    setCustodyType('standard'); setCustodyReason('temporary'); setLicenseNumber(''); setOwnerDifferent(false);
    setOwner({ name: '', address: '', phone: '', licenseNumber: '', shootingLicense: '', huntingLicense: '' });
    setFirearmType('Pistola'); setBrand(''); setModel(''); setCaliber(''); setSerialNumber(''); setCondition('new');
    setContractNumber(''); setPrice(''); setDeposit(''); setDepositTouched(false); setDepositMethod('cash');
    setStartDate(todayInput()); setDueDate(''); setDueTouched(false); setNotes(''); setSendReceipt(true);
    if (member) {
      setFoundMember(member); setLookupState('found');
      setPhone(member.phone || ''); setMemberName(member.fullName || ''); setMemberEmail(member.email || '');
      setLicenseNumber(member.licenseNumber || member.gunLicense || '');
    } else {
      setFoundMember(null); setLookupState('idle'); setPhone(''); setMemberName(''); setMemberEmail('');
    }
  }, [open, member]);

  // Fecha límite automática = inicio + termDays (editable). Custodia: "pagado
  // hasta" = depósito (sin mes gratis; los meses pagados hoy la mueven después)
  // o depósito + 180 días si es por fallecimiento.
  // OJO: `open` está en las dependencias a propósito. El diálogo queda montado
  // al cerrarse; al abrirlo de nuevo el reset deja dueDate='' pero startDate y
  // dueTouched no cambian, así que sin `open` el efecto no corría y la fecha
  // límite salía vacía (audio de Afía 2026-10-16: "no se añaden los 4 meses
  // al instante, tenemos que editarlo nosotros").
  useEffect(() => {
    if (!open) return;
    if (!dueTouched && startDate) setDueDate(addDaysInput(startDate, kind === 'custody' ? (custodyType === 'deceased' ? (settings.deceasedFeeDays || 180) : 0) : (settings.termDays || 120)));
  }, [open, startDate, settings.termDays, settings.deceasedFeeDays, dueTouched, kind, custodyType]);

  const custody = kind === 'custody';
  const deceased = custody && custodyType === 'deceased';
  const validFirearms = firearms.filter((f) => f.brand.trim() || f.serialNumber.trim());
  const monthlyFee = (Number(settings.storageFeeCents) || 0) * Math.max(1, validFirearms.length);
  const monthlyTax = settings.storageFeeTaxable ? calcTaxCents(monthlyFee) : 0;
  const deceasedFee = (Number(settings.deceasedFeeCents) || 0) * Math.max(1, validFirearms.length);
  const deceasedTax = settings.deceasedFeeTaxable !== false ? calcTaxCents(deceasedFee) : 0;
  const setFirearm = (i, k, v) => setFirearms((arr) => arr.map((f, j) => (j === i ? { ...f, [k]: v } : f)));

  // Depósito sugerido según condición (editable)
  const basePriceCents = dollarsToCents(price);
  const priceTaxCents = settings.priceTaxable === false ? 0 : calcTaxCents(basePriceCents);
  const priceCents = basePriceCents + priceTaxCents; // total con IVU: sobre esto va el depósito y el balance
  const suggestedPct = condition === 'used' ? settings.usedDepositPct : settings.newDepositPct;
  useEffect(() => {
    if (!depositTouched) setDeposit(priceCents > 0 ? centsToDollars(Math.round(priceCents * (Number(suggestedPct) || 0) / 100)) : '');
  }, [priceCents, suggestedPct, depositTouched]);

  const depositCents = dollarsToCents(deposit);
  const balanceCents = Math.max(0, priceCents - depositCents);

  const lookup = async () => {
    const d = phoneDigits(phone);
    if (d.length < 7) { setError('Escribe un teléfono válido para buscar al cliente'); return; }
    setError(''); setLookupState('searching'); setFoundMember(null);
    try {
      let snap = await getDocs(query(collection(db, 'members'), where('phone', '==', d)));
      if (snap.empty && phone !== d) snap = await getDocs(query(collection(db, 'members'), where('phone', '==', phone)));
      if (snap.empty) {
        // teléfonos guardados con formato: escaneo acotado por los últimos 4 dígitos no es posible sin índice;
        // se intenta con el formato 787-555-1234
        const formatted = d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : null;
        if (formatted) snap = await getDocs(query(collection(db, 'members'), where('phone', '==', formatted)));
      }
      if (snap.empty) { setLookupState('notfound'); return; }
      const m = pickBestMember(snap.docs, { phone: d });
      setFoundMember(m); setMemberName(m.fullName || ''); setMemberEmail(m.email || ''); setLookupState('found');
      if (!licenseNumber) setLicenseNumber(m.licenseNumber || m.gunLicense || '');
    } catch (e) { setError(e.message); setLookupState('idle'); }
  };

  const pinOk = lookupState !== 'notfound' || /^\d{4}$/.test(pin);
  const canSave = useMemo(() => {
    if (!(memberName.trim() && startDate && dueDate && pinOk)) return false;
    if (custody) return validFirearms.length > 0;
    return basePriceCents > 0 && depositCents >= 0 && depositCents <= priceCents && brand.trim() && serialNumber.trim();
  }, [custody, validFirearms.length, basePriceCents, depositCents, priceCents, memberName, brand, serialNumber, startDate, dueDate, pinOk]);

  const save = async () => {
    if (!canSave) { setError(custody ? 'Completa el cliente y al menos un arma (marca o número de serie)' : 'Completa cliente, marca, número de serie, precio y fechas'); return; }
    setSaving(true); setError('');
    try {
      const res = await layawayApi.create({
        memberId: foundMember ? foundMember.id : null,
        memberName: memberName.trim(),
        memberPhone: phoneDigits(phone),
        memberEmail: memberEmail.trim().toLowerCase(),
        memberAddress: memberAddress.trim(),
        pin: lookupState === 'notfound' ? pin : undefined,
        kind,
        ...(custody ? {
          firearms: validFirearms.map((f) => ({ ...f, brand: f.brand.trim(), model: f.model.trim(), caliber: f.caliber.trim(), serialNumber: f.serialNumber.trim() })),
          initialMonths, custodyType, custodyReason, licenseNumber: licenseNumber.trim(),
          owner: ownerDifferent ? owner : null,
        } : {}),
        firearmType, brand: brand.trim(), model: model.trim(), caliber: caliber.trim(), serialNumber: serialNumber.trim(),
        condition, contractNumber: contractNumber.trim(),
        basePriceCents, depositCents, depositMethod,
        startDate: `${startDate}T12:00:00`, dueDate: `${dueDate}T12:00:00`,
        notes, sendReceipt,
      });
      // Paso final: imprimir el contrato para la firma y/o ir a la ficha.
      setCreated({ id: res.id, layaway: { id: res.id, ...(res.layaway || {}), memberAddress: memberAddress.trim() }, accountCreated: Boolean(res.accountCreated), pin: res.pin });
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const finish = () => {
    if (created && onCreated) onCreated(created.id, created.layaway);
    onClose();
  };

  if (created) {
    return (
      <Dialog open={open} onClose={finish} maxWidth="sm" fullWidth>
        <DialogTitle>{created.layaway.kind === 'custody' ? 'Custodia creada' : 'Layaway creado'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="success">
              {created.layaway.kind === 'custody' ? <>Custodia de <b>{created.layaway.firearmCount} arma{created.layaway.firearmCount === 1 ? '' : 's'}</b></> : <>Layaway de <b>{[created.layaway.brand, created.layaway.model].filter(Boolean).join(' ')}</b></>} para <b>{created.layaway.memberName}</b> guardado.
            </Alert>
            {created.accountCreated && (
              <Alert severity="info">
                Se creó la cuenta del cliente con el teléfono <b>{phone}</b> y el PIN <b style={{ fontSize: 18, letterSpacing: 2 }}>{created.pin}</b>.
                Dáselo al cliente: con teléfono + PIN entra en la app o en bbtc-dashboard.web.app/layaways para ver y abonar.
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              Imprime el contrato con los datos ya llenos para que el cliente lo firme. También puedes imprimirlo después desde la ficha.
              {created.layaway.kind === 'custody' && <> Para la Policía imprime también el <b>Traspaso a Armería</b> (quien entrega firma; a la derecha va el propietario / fallecido).</>}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={finish}>Ir a la ficha</Button>
          {created.layaway.kind === 'custody' && <Button variant="outlined" startIcon={<Iconify icon="eva:file-text-outline" />} onClick={() => printCustodyTransfer(created.layaway, settings)}>Imprimir traspaso</Button>}
          <Button variant="contained" startIcon={<Iconify icon="eva:printer-outline" />} onClick={() => printLayawayContract(created.layaway, settings)}>Imprimir contrato</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{custody ? 'Nueva custodia de armas' : 'Nuevo layaway de arma'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <ToggleButtonGroup exclusive fullWidth size="small" value={kind} onChange={(e, v) => { if (v) { setKind(v); setDueTouched(false); } }}>
            <ToggleButton value="layaway">Layaway (compra a plazos)</ToggleButton>
            <ToggleButton value="custody">Custodia (almacenamiento)</ToggleButton>
          </ToggleButtonGroup>
          {custody && (
            <>
              <ToggleButtonGroup exclusive fullWidth size="small" value={custodyType} onChange={(e, v) => { if (v) { setCustodyType(v); setDueTouched(false); setInitialMonths(v === 'deceased' ? 0 : 1); setCustodyReason(v === 'deceased' ? 'deceased' : 'temporary'); if (v === 'deceased') setOwnerDifferent(true); } }}>
                <ToggleButton value="standard">Temporera / licencia vencida</ToggleButton>
                <ToggleButton value="deceased">Fallecimiento / inscripción de arma de fuego</ToggleButton>
              </ToggleButtonGroup>
              {deceased ? (
                <Alert severity="info">
                  Cláusula 8: se cobra <b>{formatPrice(settings.deceasedFeeCents)} por arma</b>{settings.deceasedFeeTaxable !== false ? ' + IVU' : ''} por los trámites con la Policía, e <b>incluye {settings.deceasedFeeDays || 180} días de almacenaje</b>. Después aplica el almacenaje normal de {formatPrice(settings.storageFeeCents)} por arma al mes.
                </Alert>
              ) : (
                <Alert severity="info">
                  Una custodia puede tener varias armas. No hay precio ni balance: el cliente paga el almacenaje de <b>{formatPrice(settings.storageFeeCents)} por arma al mes</b>{settings.storageFeeTaxable ? ' + IVU' : ''}, todas en un solo pago, <b>desde el primer mes</b>.
                </Alert>
              )}
            </>
          )}
          <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth label="Teléfono" value={phone} onChange={(e) => { setPhone(e.target.value); setLookupState('idle'); }}
                onBlur={() => { if (!member && lookupState === 'idle') lookup(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookup(); } }}
                disabled={Boolean(member)}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    {lookupState === 'searching' ? <CircularProgress size={18} /> : (!member && <Button size="small" onClick={lookup}>Buscar</Button>)}
                  </InputAdornment>
                ) }}
                helperText={lookupState === 'found' ? `Cuenta encontrada${foundMember && foundMember.memberId ? ` · #${foundMember.memberId}` : ''}` : lookupState === 'notfound' ? 'No hay cuenta con ese teléfono: se le creará la cuenta al cliente (teléfono + PIN) para que pueda entrar en la app.' : 'Busca la cuenta del cliente por teléfono'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Nombre completo" value={memberName} onChange={(e) => setMemberName(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Email (recibos)" value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)} />
            </Grid>
            {lookupState === 'notfound' && (
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="PIN de la cuenta nueva" value={pin} inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  error={pin.length !== 4} helperText="4 dígitos. Con teléfono + PIN el cliente entra en la app."
                />
              </Grid>
            )}
            <Grid item xs={12} sm={lookupState === 'notfound' ? 9 : 12}>
              <TextField fullWidth label="Dirección (sale en el contrato)" value={memberAddress} onChange={(e) => setMemberAddress(e.target.value)} />
            </Grid>
          </Grid>

          {custody && (
            <>
              <Typography variant="subtitle2" color="text.secondary">Armas en custodia ({validFirearms.length})</Typography>
              {firearms.map((f, i) => (
                <Grid container spacing={1} key={i} alignItems="center">
                  <Grid item xs={6} sm={2}>
                    <TextField select fullWidth size="small" label="Tipo" value={f.firearmType} onChange={(e) => setFirearm(i, 'firearmType', e.target.value)}>
                      {FIREARM_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Marca *" value={f.brand} onChange={(e) => setFirearm(i, 'brand', e.target.value)} /></Grid>
                  <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Modelo" value={f.model} onChange={(e) => setFirearm(i, 'model', e.target.value)} /></Grid>
                  <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Calibre" value={f.caliber} onChange={(e) => setFirearm(i, 'caliber', e.target.value)} /></Grid>
                  <Grid item xs={10} sm={3}><TextField fullWidth size="small" label="Núm. de serie *" value={f.serialNumber} onChange={(e) => setFirearm(i, 'serialNumber', e.target.value)} /></Grid>
                  <Grid item xs={2} sm={1}>
                    <Tooltip title="Quitar"><span><IconButton size="small" color="error" disabled={firearms.length === 1} onClick={() => setFirearms((arr) => arr.filter((_, j) => j !== i))}><Iconify icon="eva:trash-2-outline" /></IconButton></span></Tooltip>
                  </Grid>
                </Grid>
              ))}
              <div><Button size="small" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setFirearms((arr) => [...arr, emptyFirearm()])}>Añadir otra arma</Button></div>
              <Typography variant="subtitle2" color="text.secondary">Documento de traspaso a la armería (Policía de Puerto Rico)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField select fullWidth label="Motivo del traspaso" value={custodyReason} onChange={(e) => setCustodyReason(e.target.value)} helperText="Casilla que se marca en el documento">
                    {Object.entries(CUSTODY_REASON).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Lic. de armas de quien entrega" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} helperText="El cliente de la custodia es quien entrega y firma" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel control={<Switch checked={ownerDifferent} onChange={(e) => setOwnerDifferent(e.target.checked)} />} label="El propietario / fallecido es otra persona" />
                </Grid>
                {ownerDifferent && (
                  <>
                    <Grid item xs={12}><Alert severity="info" sx={{ py: 0 }}>Estos datos van en la sección <b>Información del Fallecido / Propietario del arma</b> del documento.</Alert></Grid>
                    <Grid item xs={12} sm={5}><TextField fullWidth size="small" label="Nombre del propietario / fallecido" value={owner.name} onChange={(e) => setOwner({ ...owner, name: e.target.value })} /></Grid>
                    <Grid item xs={12} sm={7}><TextField fullWidth size="small" label="Dirección" value={owner.address} onChange={(e) => setOwner({ ...owner, address: e.target.value })} /></Grid>
                    <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Teléfono" value={owner.phone} onChange={(e) => setOwner({ ...owner, phone: e.target.value })} /></Grid>
                    <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Lic. Armas" value={owner.licenseNumber} onChange={(e) => setOwner({ ...owner, licenseNumber: e.target.value })} /></Grid>
                    <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Lic. Tiro" value={owner.shootingLicense} onChange={(e) => setOwner({ ...owner, shootingLicense: e.target.value })} /></Grid>
                    <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Lic. Caza" value={owner.huntingLicense} onChange={(e) => setOwner({ ...owner, huntingLicense: e.target.value })} /></Grid>
                  </>
                )}
              </Grid>
              <Typography variant="subtitle2" color="text.secondary">Plazo y almacenaje</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth type="date" label="Fecha de depósito" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth type="date" label="Pagado hasta" value={dueDate} onChange={(e) => { setDueDate(e.target.value); setDueTouched(true); }} InputLabelProps={{ shrink: true }} helperText={deceased ? `Trámite incluye ${settings.deceasedFeeDays || 180} días` : 'Más los meses pagados hoy'} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField select fullWidth label="Meses pagados hoy" value={initialMonths} onChange={(e) => setInitialMonths(Number(e.target.value))} helperText={initialMonths > 0 ? `Mueve "pagado hasta" ${initialMonths * (settings.storageMonthDays || 30)} días` : (deceased ? 'Opcional (ya incluye 180 días)' : 'Sin pagar queda vencida de inmediato')}>
                    {[0, 1, 2, 3, 6, 12].map((m) => <MenuItem key={m} value={m}>{m === 0 ? 'Ninguno' : `${m} mes${m > 1 ? 'es' : ''} · ${formatPrice((monthlyFee + monthlyTax) * m)}`}</MenuItem>)}
                  </TextField>
                </Grid>
                {(initialMonths > 0 || deceased) && (
                  <Grid item xs={6} sm={3}>
                    <TextField select fullWidth label="Pagado con" value={depositMethod} onChange={(e) => setDepositMethod(e.target.value)}>
                      {Object.entries(PAYMENT_METHOD).filter(([k]) => k !== 'card_online').map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Núm. de contrato (opcional)" value={contractNumber} onChange={(e) => setContractNumber(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth multiline minRows={2} label="Notas internas" value={notes} onChange={(e) => setNotes(e.target.value)} />
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {deceased && <Chip size="small" color="primary" label={`Trámite hoy: ${formatPrice(deceasedFee)}${deceasedTax ? ` + IVU = ${formatPrice(deceasedFee + deceasedTax)}` : ''} (${Math.max(1, validFirearms.length)} arma${validFirearms.length === 1 ? '' : 's'})`} />}
                <Chip size="small" variant="outlined" label={`Almacenaje mensual: ${formatPrice(monthlyFee)}${monthlyTax ? ` + IVU = ${formatPrice(monthlyFee + monthlyTax)}` : ''} (${Math.max(1, validFirearms.length)} arma${validFirearms.length === 1 ? '' : 's'})`} />
                {(initialMonths > 0 || deceased) && <Chip size="small" color="success" label={`A cobrar hoy: ${formatPrice((deceased ? deceasedFee + deceasedTax : 0) + (monthlyFee + monthlyTax) * initialMonths)}`} />}
              </Stack>
            </>
          )}

          {!custody && (<>
          <Typography variant="subtitle2" color="text.secondary">Arma (según el contrato)</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth label="Tipo" value={firearmType} onChange={(e) => setFirearmType(e.target.value)}>
                {FIREARM_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Marca *" value={brand} onChange={(e) => setBrand(e.target.value)} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Modelo" value={model} onChange={(e) => setModel(e.target.value)} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth label="Calibre" value={caliber} onChange={(e) => setCaliber(e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Núm. de serie *" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} /></Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth label="Condición" value={condition} onChange={(e) => setCondition(e.target.value)} helperText={`Depósito sugerido: ${suggestedPct}%`}>
                <MenuItem value="new">Nueva</MenuItem>
                <MenuItem value="used">Usada</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}><TextField fullWidth label="Núm. de contrato (opcional)" value={contractNumber} onChange={(e) => setContractNumber(e.target.value)} /></Grid>
          </Grid>

          <Typography variant="subtitle2" color="text.secondary">Dinero y plazo</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Precio del arma (sin IVU) *" value={price} onChange={(e) => setPrice(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} helperText={priceTaxCents > 0 ? `+ IVU ${formatPrice(priceTaxCents)} = total ${formatPrice(priceCents)}` : 'Precio del contrato'} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Depósito inicial" value={deposit} onChange={(e) => { setDeposit(e.target.value); setDepositTouched(true); }} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} helperText={depositCents < Math.round(priceCents * suggestedPct / 100) && priceCents > 0 ? `Menor al ${suggestedPct}% del contrato` : ' '} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth label="Pagado con" value={depositMethod} onChange={(e) => setDepositMethod(e.target.value)}>
                {Object.entries(PAYMENT_METHOD).filter(([k]) => k !== 'card_online').map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Stack spacing={0.5} sx={{ pt: 1 }}>
                <Typography variant="caption" color="text.secondary">Total con IVU · Balance</Typography>
                <Typography variant="h6" color={balanceCents > 0 ? 'error.main' : 'success.main'}>{formatPrice(priceCents)} · {formatPrice(balanceCents)}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type="date" label="Fecha de separación" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth type="date" label="Fecha límite" value={dueDate} onChange={(e) => { setDueDate(e.target.value); setDueTouched(true); }} InputLabelProps={{ shrink: true }} helperText={`${settings.termDays} días por contrato`} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline minRows={2} label="Notas internas" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <FormControlLabel control={<Switch checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} />} label="Enviar recibo del depósito por email" />
            {settings.storageFeeCents > 0 && (
              <Chip size="small" variant="outlined" label={`Almacenaje después de la fecha límite: ${formatPrice(settings.storageFeeCents)}/mes${settings.storageFeeTaxable ? ` + IVU (${formatPrice(settings.storageFeeCents + calcTaxCents(settings.storageFeeCents))})` : ''}`} />
            )}
          </Stack>
          </>)}
          {custody && <FormControlLabel control={<Switch checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} />} label="Enviar recibo por email (si paga meses hoy)" />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving || !canSave}>{saving ? 'Guardando…' : custody ? 'Crear custodia' : 'Crear layaway'}</Button>
      </DialogActions>
    </Dialog>
  );
}

LayawayCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  member: PropTypes.object,
};
